import { GigType } from "model/Gig";

import type {
  FormData,
  RecruitFormData,
  SelectedWorker,
} from "../modal/gigFormModal.types";

export const GIG_DESCRIPTION_MAX_LENGTH = 2056;

type ValidateGigFormInput = {
  readonly formData: FormData;
  readonly hasWorkersOutsideEmployment: boolean;
  readonly recruitFormData: RecruitFormData;
  readonly selectedWorkers: readonly SelectedWorker[];
  readonly showOverlappingError: boolean;
};

type ValidationFactsInput = Omit<ValidateGigFormInput, "showOverlappingError">;

type DisabledReasonKey =
  | "CreateDisabledWorkersMissing"
  | "CreateDisabledWorkersMissingTeam"
  | "CreateDisabledShiftsMissing"
  | "CreateDisabledOverlappingShifts"
  | "CreateDisabledWorkersOutsideEmployment"
  | "CreateDisabledRoleMissing"
  | "CreateDisabledWorkGroupMissing"
  | "CreateDisabledRatesInvalid"
  | "CreateDisabledCompleteRequiredFields";

type ValidationFacts = {
  readonly descOk: boolean;
  readonly hasWorkers: boolean;
  readonly hasWorkersOutsideEmployment: boolean;
  readonly hasWorkersWithTeams: boolean;
  readonly ratesOk: boolean;
  readonly roleOk: boolean;
  readonly shiftsOk: boolean;
  readonly workGroupOk: boolean;
};

const assertUnreachableGigType = (gigType: never): never => {
  throw new Error(`Unhandled create gig type: ${String(gigType)}`);
};

const computeValidationFacts = (
  input: ValidationFactsInput,
): ValidationFacts => {
  const shiftsOk = (input.formData.shifts?.length ?? 0) > 0;
  const descOk =
    (input.formData.description?.length ?? 0) <= GIG_DESCRIPTION_MAX_LENGTH;
  const hasWorkers = input.selectedWorkers.length > 0;
  const hasWorkersWithTeams =
    hasWorkers &&
    input.selectedWorkers.every(
      (worker) =>
        worker.selectedTeamId !== null && worker.selectedTeamId !== undefined,
    );
  const roleOk =
    typeof input.recruitFormData.role === "string" &&
    input.recruitFormData.role.trim().length > 0 &&
    input.recruitFormData.role.length <= 24;
  const workGroupOk =
    input.recruitFormData.workGroupId !== null &&
    input.recruitFormData.workGroupId !== undefined;
  const min = Number(input.recruitFormData.minRate);
  const max = Number(input.recruitFormData.maxRate);
  const ratesOk = Number.isFinite(min) && Number.isFinite(max) && max >= min;

  return {
    descOk,
    hasWorkers,
    hasWorkersOutsideEmployment: input.hasWorkersOutsideEmployment,
    hasWorkersWithTeams,
    ratesOk,
    roleOk,
    shiftsOk,
    workGroupOk,
  };
};

/**
 * Validates gig form state based on gig type specific business rules.
 */
export const validateGigForm = ({
  formData,
  hasWorkersOutsideEmployment: employmentConflictInput,
  recruitFormData,
  selectedWorkers,
  showOverlappingError,
}: ValidateGigFormInput): boolean => {
  const {
    shiftsOk,
    descOk,
    hasWorkersOutsideEmployment,
    hasWorkersWithTeams,
    roleOk,
    workGroupOk,
    ratesOk,
  } = computeValidationFacts({
    formData,
    hasWorkersOutsideEmployment: employmentConflictInput,
    recruitFormData,
    selectedWorkers,
  });
  if (!shiftsOk || !descOk) return false;

  switch (formData.type) {
    case GigType.SOLO:
      return (
        hasWorkersWithTeams &&
        !hasWorkersOutsideEmployment &&
        !showOverlappingError
      );
    case GigType.WORKPOOL:
      return roleOk && workGroupOk;
    case GigType.PUBLIC:
      return roleOk && workGroupOk && ratesOk;
  }

  return assertUnreachableGigType(formData.type);
};

/**
 * Returns translation keys for why create action is disabled.
 * Mirrors validateGigForm rules per gig type.
 */
export const getCreateDisabledReasonKeys = ({
  formData,
  hasWorkersOutsideEmployment: employmentConflictInput,
  recruitFormData,
  selectedWorkers,
  showOverlappingError,
}: ValidateGigFormInput): DisabledReasonKey[] => {
  const reasons: DisabledReasonKey[] = [];
  const {
    shiftsOk,
    descOk,
    hasWorkers,
    hasWorkersOutsideEmployment,
    hasWorkersWithTeams,
    roleOk,
    workGroupOk,
    ratesOk,
  } = computeValidationFacts({
    formData,
    hasWorkersOutsideEmployment: employmentConflictInput,
    recruitFormData,
    selectedWorkers,
  });

  if (!shiftsOk) {
    reasons.push("CreateDisabledShiftsMissing");
  }
  if (!descOk) {
    reasons.push("CreateDisabledCompleteRequiredFields");
  }

  switch (formData.type) {
    case GigType.SOLO:
      if (!hasWorkers) {
        reasons.push("CreateDisabledWorkersMissing");
      } else if (!hasWorkersWithTeams) {
        reasons.push("CreateDisabledWorkersMissingTeam");
      }
      if (hasWorkersOutsideEmployment) {
        reasons.push("CreateDisabledWorkersOutsideEmployment");
      }
      if (showOverlappingError) {
        reasons.push("CreateDisabledOverlappingShifts");
      }
      break;
    case GigType.WORKPOOL:
      if (!roleOk) {
        reasons.push("CreateDisabledRoleMissing");
      }
      if (!workGroupOk) {
        reasons.push("CreateDisabledWorkGroupMissing");
      }
      break;
    case GigType.PUBLIC:
      if (!roleOk) {
        reasons.push("CreateDisabledRoleMissing");
      }
      if (!workGroupOk) {
        reasons.push("CreateDisabledWorkGroupMissing");
      }
      if (!ratesOk) {
        reasons.push("CreateDisabledRatesInvalid");
      }
      break;
  }

  if (reasons.length === 0) {
    reasons.push("CreateDisabledCompleteRequiredFields");
  }

  return [...new Set(reasons)];
};
